import React, { type PropsWithChildren } from 'react';

import { type AppContextProviderProps } from '../../common/ui/app-context';
import { WidthObserverProvider } from '../../common/ui/width-observer';
import { type BootstrapContainerProps } from '../../controllers/store/bootstrap/bootstrap-store-provider';
import Filters, { StackedFilters } from './filters';
import Input from './input';
import { Layout } from './layout';
import { ResultsContainer } from './results';
import { Shell } from './shell';

export type SearchPageProps = {
	callbacks?: BootstrapContainerProps['callbacks'];
	shell: Omit<BootstrapContainerProps, 'products'>;
} & AppContextProviderProps;
/**
 * This is the main component which is exposed from the dynamic-cards-search-page
 * package.
 * The motivation is for consumers do a one-component bootstrap.
 */
export const SearchPage = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	linkComponent,
}: PropsWithChildren<SearchPageProps>): React.JSX.Element => (
	<Shell
		{...shell}
		callbacks={callbacks}
		mapUserIdToProfileUrl={mapUserIdToProfileUrl}
		linkComponent={linkComponent}
	>
		<WidthObserverProvider>
			<Layout
				components={{
					filters: <Filters />,
					results: <ResultsContainer />,
				}}
			/>
		</WidthObserverProvider>
	</Shell>
);

export type CustomSearchMacroConfigFiltersProps = {
	filtersToHide: string[];
} & SearchPageProps;

/**
 * This component is used by Company Hub's custom search macro. It renders the search filters
 * for use within the related config modal.
 */
export const CustomSearchMacroConfigFilters = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	linkComponent,
	filtersToHide,
}: PropsWithChildren<CustomSearchMacroConfigFiltersProps>): React.JSX.Element => (
	<Shell
		{...shell}
		callbacks={callbacks}
		mapUserIdToProfileUrl={mapUserIdToProfileUrl}
		linkComponent={linkComponent}
	>
		<WidthObserverProvider>
			<Layout
				components={{
					filters: <StackedFilters filtersToHide={filtersToHide} />,
				}}
				disableHeaderGridStyles
			/>
		</WidthObserverProvider>
	</Shell>
);

/**
 * This component is used by Company Hub's custom search macro.
 * It renders a search input for use within the editor.
 */
export const SearchInput = ({
	shell,
	callbacks,
	mapUserIdToProfileUrl,
	generateSearchUrl,
	linkComponent,
}: PropsWithChildren<SearchPageProps>): React.JSX.Element => (
	<Shell
		{...shell}
		callbacks={callbacks}
		mapUserIdToProfileUrl={mapUserIdToProfileUrl}
		generateSearchUrl={generateSearchUrl}
		linkComponent={linkComponent}
	>
		<WidthObserverProvider>
			<Input />
		</WidthObserverProvider>
	</Shell>
);
